import { useParams } from '@tanstack/react-router'

import type { CoverProduct, Enriched, EnrichedRangeData, SofaItem } from '@sofa-platform/types'

import { useRangeData } from './use-range-data'

type CurrentSofaData = {
  currentCoverProduct?: Enriched<CoverProduct>
  id: string
  rangeId: string
} & Enriched<SofaItem>

type CurrentSofaState = {
  rangeId: string
  rangeData: EnrichedRangeData
  sofa: CurrentSofaData | undefined
}

/**
 * Hook to access the current sofa based on the URL parameters.
 *
 * @param guaranteedSelection - If true, throws an error when rangeId or id is missing and guarantees a defined sofa. Default is false.
 * @returns The current range and sofa, or undefined when rangeId is missing and guaranteedSelection is false.
 */
export function useCurrentSofa(guaranteedSelection: true): CurrentSofaState & { sofa: CurrentSofaData }
export function useCurrentSofa(guaranteedSelection?: false): CurrentSofaState | undefined
export function useCurrentSofa(guaranteedSelection = false): CurrentSofaState | undefined {
  const { id, rangeId } = useParams({ strict: false })
  const { data: ranges } = useRangeData()

  if (!rangeId) {
    if (guaranteedSelection) {
      throw new Error(
        'useCurrentSofa was called with guaranteedSelection=true, but rangeId is missing from the URL params.',
      )
    }
    return undefined
  }

  const rangeData = ranges[rangeId]
  if (!rangeData) {
    throw new Error(`Range with id: ${rangeId} not found`)
  }

  if (!id) {
    if (guaranteedSelection) {
      throw new Error('useCurrentSofa was called with guaranteedSelection=true, but id is missing from the URL params.')
    }
    return { rangeId, rangeData, sofa: undefined }
  }

  const currentSofa = rangeData.sofas.find((sofa) => sofa.itemNo === id)
  if (!currentSofa) {
    throw new Error(`Sofa with id: ${id} in ${rangeId} not found`)
  }

  const currentCoverProduct = getCoverProductFromProduct(currentSofa.childItems, rangeData)

  return {
    rangeId,
    rangeData,
    sofa: { ...currentSofa, currentCoverProduct, id, rangeId },
  }
}

function getCoverProductFromProduct(childItems: string[] | undefined, rangeData: EnrichedRangeData) {
  if (!rangeData.coverProducts || !childItems) return
  return rangeData.coverProducts.find((item) => childItems.includes(item.itemNo))
}
